import React, { useState, useContext } from 'react';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import Note from './Note';
import ArrowMenu from '../../../../../ArrowMenu';
import Checkbox from '../../../../../Form/Checkbox';

import getResizedImageUrl from '../../../../../../helpers/getResizedImageUrl';
import { JobsContext } from '../../../../../../context/JobsContext';
import { CandidatesContext } from '../../../../../../context/CandidatesContext';
import classes from './styles.module.scss';
import DefaultAvatar from '../../DefaultAvatar';
import FavStatus from '../../../../Header/Search/FavStatus';
import { useUserStore } from '../../../../../../store';

export default function CandidateGridCard({
  reference,
  candidate,
  refetch,
  toggleCandidateSelection,
  selectedRows,
  showCandidateInfo,
}) {
  const isClient = useUserStore((state) => state.isClient);
  const [isArrowMenuVisible, setIsArrowMenuVisible] = useState(false);

  const { showShareModal } = useContext(JobsContext);
  const {
    showAddCandidateNoteModal,
    showInviteToJobModal,
    showSubmitCandidateModal,
  } = useContext(CandidatesContext);

  const { t } = useTranslation();

  const sendEmailToCandidate = () => {
    if (!candidate.userProfile?.email) {
      return;
    }
    window.location.href = `mailto:${candidate.userProfile?.email}`;
  };

  const options = isClient
    ? []
    : [
        {
          label: t(
            'dashboardComponents.Candidates.CandidatesList.AllCandidates.CandidateGridCard.options.submitCandidate'
          ),
          onClick: () => showSubmitCandidateModal(candidate, refetch),
        },
        {
          label: t(
            'dashboardComponents.Candidates.CandidatesList.AllCandidates.CandidateGridCard.options.inviteToJob'
          ),
          onClick: () => showInviteToJobModal(candidate, refetch),
        },
        {
          label: candidate?.note
            ? t(
                'dashboardComponents.Candidates.CandidatesList.AllCandidates.CandidateGridCard.options.editNote'
              )
            : t(
                'dashboardComponents.Candidates.CandidatesList.AllCandidates.CandidateGridCard.options.addNote'
              ),
          onClick: () => showAddCandidateNoteModal(candidate),
          withBorder: true,
        },
        {
          label: t(
            'dashboardComponents.Candidates.CandidatesList.AllCandidates.CandidateGridCard.options.emailCandidate'
          ),
          onClick: () => sendEmailToCandidate(),
        },
        {
          label: t(
            'dashboardComponents.Candidates.CandidatesList.AllCandidates.CandidateGridCard.options.share'
          ),
          onClick: () => showShareModal({ candidate }),
        },
      ];

  const isJoined = candidate.jobApplications?.some?.(
    (jobApp) => jobApp.subStatus === 'Joined'
  );

  if (isJoined) {
    options.splice(0, 1);
  }

  return (
    <div className={classes.container} ref={reference}>
      <div className={classes.border} />
      <div
        className={classNames(classes.CandidateGridCard, {
          [classes.joined]: isJoined,
        })}
        onClick={() => {
          showCandidateInfo(candidate.id);
        }}
      >
        {!isClient && candidate.note && <Note text={candidate.note} />}
        <header className={classes.header}>
          {!isClient && (
            <Checkbox
              isSelected={selectedRows.includes(candidate.id)}
              onChange={(e) => {
                e.stopPropagation();
                toggleCandidateSelection(candidate.id);
              }}
            />
          )}
          <div className={classes.buttons}>
            <FavStatus
              candidate={candidate}
              refetch={refetch}
              white={isJoined}
            />
            {!isClient && (
              <ArrowMenu
                options={options}
                isVisible={isArrowMenuVisible}
                setIsVisible={setIsArrowMenuVisible}
              >
                <div className={classes.threeDots} />
              </ArrowMenu>
            )}
          </div>
        </header>
        <div className={classes.photoBlock}>
          {candidate.userProfile?.avatar ? (
            <img
              src={getResizedImageUrl({
                url: `avatar/${candidate.userProfile.avatar}`,
                width: 160,
              })}
              alt="Candidate"
              className={classes.defaultAvatarContainer}
            />
          ) : (
            <div className={classes.defaultAvatarContainer}>
              <DefaultAvatar
                firstName={candidate.userProfile?.firstName}
                lastName={candidate.userProfile?.lastName}
              />
            </div>
          )}
          <h3 className={classes.name}>
            {candidate.userProfile?.firstName} {candidate.userProfile?.lastName}
          </h3>
          <span className={classes.location}>
            {candidate.userProfile?.firstName} {candidate.userProfile?.lastName}
          </span>
        </div>
        <div className={classes.candidateDetails}>
          <div className={classes.jobsApplied}>
            <span className={classes.title}>
              {t(
                'dashboardComponents.Candidates.CandidatesList.AllCandidates.CandidateGridCard.labels.jobsApplied'
              )}
            </span>
            <div
              className={classNames(classes.count, {
                [classes.zero]: candidate.jobApplications?.length === 0,
              })}
            >
              {candidate.jobApplications?.length ?? 0}
            </div>
          </div>
          <div className={classes.email}>{candidate.userProfile?.email}</div>
          <div className={classes.phone}>{candidate.userProfile?.phone}</div>
        </div>
      </div>
    </div>
  );
}
